import { z, Schema } from 'zod';

import {
  Channel,
  ChannelSchema,
  VersionString,
  VersionStringSchema,
  ConfigurationStatusType,
  ConfigurationStatusSchema,
  FieldValue,
  FieldValueSchema
} from './schema';

import {
  EnrichmentFieldConfiguration,
  EnrichmentFieldConfigurationSchema
} from './enrichment';

import { Schedule, ScheduleSchema, Variant, VariantSchema } from './variant';

export type SelectionGroup = {
  readonly key: string;
  readonly label: string;
  readonly default?: ModuleConfiguration;
  readonly schedule?: Schedule;
  readonly selections: Readonly<Variant[]>;
};

export const SelectionGroupSchema: Schema<SelectionGroup> = z.lazy(() =>
  z.object({
    key: z.string(),
    label: z.string(),
    default: ModuleConfigurationSchema.optional(),
    schedule: ScheduleSchema.optional(),
    selections: z.array(VariantSchema)
  })
);

export type ZoneConfiguration = Readonly<SelectionGroup[]>;

export const ZoneConfigurationSchema: Schema<ZoneConfiguration> =
  z.array(SelectionGroupSchema);

/**
 * Structure of a module configuration.
 * @typedef ModuleConfiguration
 * @property - type - A string that identifies which module definition this module configuration is an instance of (e.g. AppCarousel). The type should be equal to the name of the definition.
 * @property - key - A natural key that can be combined with type to form a unique composite key.
 * @property - locale - A string indicating the locale of the surface/module instance.
 * @property - label - A human readable name (e.g. “App Carousel”). This is primarily used in the user interface of an authoring tool (i.e. Surface Management).
 * @property - description - A human readable description. This is primarily used in the user interface of an authoring tool.
 * @property - channel - A string indicating the desired channel this module should be delivered.
 * @property - definitionVersion - A semantic version (e.g. “2.1.0”) indicating the version of the module definition that was used to create this module instance.
 * @property - revision - A number indicating the number of times the module has been revised and published. This can be used in potential rollbacks and in analytics.
 * @property - status - States representing the lifecycle of a module.
 * @property - fields - An object of fields created from the fields in the module definition.
 * @property - enrichmentFields - Fields requiring enrichment.
 * @property - zones - An object with the zone names (if zones are present on the definition) as keys and an array of selection groups (containing module instances) as the value.
 */

export type ModuleConfiguration = {
  readonly type: string;
  readonly key: string;
  readonly locale: string;
  readonly label: string;
  readonly description?: string;
  readonly channel: Channel;
  readonly definitionVersion: VersionString;
  readonly revision: number;
  readonly status: ConfigurationStatusType;
  readonly fields: Record<string, FieldValue>;
  readonly enrichmentFields?: Record<string, EnrichmentFieldConfiguration>;
  readonly zones?: Record<string, ZoneConfiguration>;
};

export const ModuleConfigurationSchema: Schema<ModuleConfiguration> = z.object({
  type: z.string(),
  key: z.string(),
  locale: z.string(),
  label: z.string(),
  description: z.string().optional(),
  channel: ChannelSchema,
  definitionVersion: VersionStringSchema,
  revision: z.number(),
  status: ConfigurationStatusSchema,
  fields: z.record(z.string(), FieldValueSchema),
  enrichmentFields: z
    .record(z.string(), EnrichmentFieldConfigurationSchema)
    .optional(),
  zones: z.record(z.string(), ZoneConfigurationSchema).optional()
});

export type SurfaceConfiguration = {
  readonly [Property in keyof ModuleConfiguration]: ModuleConfiguration[Property];
};

export const SurfaceConfigurationSchema: Schema<SurfaceConfiguration> =
  ModuleConfigurationSchema;

/**
 * Create a surface from a surface configuration json.
 * @param configuration Surface configuration json, conforming with SurfaceConfiguration type.
 * @returns - Surface configuration object.
 * @example
 * ```ts
 *    import { createSurfaceConfiguration } from '@atlassiansox/bxp-definition-sdk';
 *
 *    export const ExampleSurface = createSurfaceConfiguration({
 *      {
          "type":"ExampleWebSurface",
          "key":"TestSurfaceV1",
          "locale":"en",
          "label":"Test Surface - v1.0.0",
          "description":"Creating a Test Surface",
          "channel":"web",
          "definitionVersion":"0.0.1",
          "revision":1,
          "status":"published",
          "fields":{
            "title":"Test Surface",
            "description":"Test Surface"
          },
          "zones":{
            "main":[{
                "key": "hello-world-variations",
                "label": "Hello World module variations",
                "default": {
                  "type":"HelloWorld",
                  "key":"HelloWorldV1",
                  "locale":"en",
                  "label":"Hello World Module - v1.0.0",
                  "description":"Hello World Module - v1.0.0",
                  "channel": "web",
                  "definitionVersion":"1.0.0",
                  "revision":1,
                  "status":"published",
                  "fields":{
                    "title":"Test Hello World Module title",
                    "size": 50,
                    "value": "some value"
                  }
                },
                "selections": []        
            }]
          }
        }
 *   });
 * ```
 */

export function createSurfaceConfiguration<
  const M extends SurfaceConfiguration
>(surfaceConfiguration: M): M {
  return SurfaceConfigurationSchema.parse(surfaceConfiguration) as M;
}
